// The front-end: which screen is up, and how you move between them.
//
// The canvas never stops rendering behind any of this. The city, the gorillas
// going about their business and whatever is crossing the sky all keep running,
// which is what makes the title screen an attract mode rather than a frozen
// picture with a logo on it.

import { state } from './state.js';
import { closePanels, screens, shellEl, splashHintEl, splashTipEl, updateLabels, updateMenuButtons, updateMusicLabel } from './dom.js';
import { audioLocked, initAudio, setMusic, setMusicDuck, setMusicMood } from './audio.js';
import { SPLASH_TIP_COUNT } from './config.js';
import { t } from './i18n.js';
import { startSplashScene } from './splash.js';

export const SCREENS = ['splash', 'title', 'menu', 'setup', 'pause', 'results', 'game'];

/** The screens where a match is under way, and so where the controls belong. */
const IN_MATCH = new Set(['game', 'pause']);

/** How long the splash holds before it hands over to the title. */
const SPLASH_MS = 1700;

let splashTimer = null;

/** Moves to a screen. Everything else is hidden, so there is no state to get wrong. */
export function showScreen(id) {
  if (!SCREENS.includes(id)) return;

  state.screen = id;
  shellEl.dataset.screen = id;

  // A match is on from the moment the game screen is. Starting one sets this in
  // newMatch, but loading a shared code goes straight to the city without going
  // through it -- and the whole front end hangs off this flag, from the round badge
  // to whether the menu offers a way back to the match.
  if (id === 'game') state.matchLive = true;

  SCREENS.forEach(name => {
    const el = screens[name];
    if (el) el.hidden = name !== id;
  });

  // Every screen change closes them. A panel is opened from a screen and closed by
  // its own button, so nothing needs one to survive a move — and leaving one open
  // over the match is how the match ends up covered by the awards list.
  closePanels();

  // The turn clock needs nothing here: it is held for exactly as long as the match
  // is not the screen being looked at, and advanceTurnClock reads that off the
  // screen every frame. There is no hold to take and none to give back.

  // The menu's buttons depend on whether there is still a match to go back to.
  updateMenuButtons();

  // And the header's round badge depends on whether a match is running at all, so
  // it is refreshed from here rather than only where the match begins and ends:
  // every screen change passes through this function, which is exactly when the
  // badge needs to lose the round of a match that has just been won.
  updateLabels();

  // The match gets the quiet take of the theme; every other screen gets the full
  // one. Switching is a cut, so it lands like a change of scene rather than a fade.
  //
  // Y la pausa no toca el volumen: la música del tablero sigue sonando igual que mientras se juega.
  // Durante un tiempo se bajaba a 0,6 en la pausa —el argumento era que el tablero sigue en pantalla
  // y se sigue leyendo— y el jugador ha pedido lo contrario: la pausa es una parada, no un cambio de
  // escena, y la música no tiene por qué enterarse. `setMusicDuck` sigue en audio.js por si algún día
  // vuelve a hacer falta; hoy no la llama nadie.
  setMusicMood(IN_MATCH.has(id) ? 'ambient' : 'loud');
}

/** True while a match is on screen, paused or not. */
export function inMatch() {
  return IN_MATCH.has(state.screen);
}

/**
 * True on a phone held upright, which is the one shape this game cannot play in.
 *
 * The same media query the stylesheet uses to put the notice up, deliberately: the notice and the
 * hold are one question asked in two places, and asking it twice from two sources is how the two
 * end up disagreeing. Ask `matchOnHold` instead of this, unless you are the notice.
 */
const upright = window.matchMedia('(orientation: portrait) and (max-width: 820px)');

export const heldUpright = () => upright.matches;

/**
 * Si esto es un teléfono, que es lo que decide si se le puede pedir que gire solo.
 *
 * Pedir pantalla completa para poder bloquear la orientación sólo tiene sentido en un teléfono, y
 * sólo ahí se gana algo: el aparato se gira solo y el aviso de «gira el móvil» no llega a verse. En
 * un ordenador esa petición no consigue nada bueno —no hay orientación que bloquear— y sí algo
 * malo: la ventana se pone a pantalla completa sin que nadie lo haya pedido. Medido, eso es lo que
 * pasaba: pulsar EMPEZAR PARTIDA dejaba `document.fullscreenElement` en el `shell` y el tablero
 * reajustado a 931 px en una ventana de 1176.
 *
 * Las dos condiciones, táctil **y** lado corto, porque una sola miente: un portátil con pantalla
 * táctil tiene la primera y no la segunda, y una tablet tiene las dos pero no es un aparato que se
gire en la mano. El 500 es holgura de sobra para el teléfono más grande —un móvil moderno tumbado
 * mide 428 de lado corto— y no llega a la tablet más pequeña, que se queda en 744.
 */
export function isPhone() {
  return window.matchMedia('(pointer: coarse)').matches
    && Math.min(window.innerWidth, window.innerHeight) <= 500;
}

/**
 * True while the match is on screen but cannot be played: a phone held upright.
 *
 * The board comes out 336x190 on a 390x844 screen — a fifth of the glass — against 660x378 the
 * moment the phone is on its side, so this is not a match anybody can play: it is a match waiting
 * for the phone to be turned. While it is true the notice covers the board (see styles.css) and
 * the two things that make a match are held — the loop stops stepping the crate, the banana and
 * the leap, and `advanceTurnClock` stops charging the turn — while the rest of the frame carries
 * on exactly as it does behind the pause: the sky, the city and the neighbours are the backdrop
 * the notice sits on.
 *
 * Read off the screen every frame rather than turned into a flag, for the same reason the pause
 * needs no flag here: turning the phone back is all it takes to give everything back.
 */
export function matchOnHold() {
  // False fuera de la partida, que es como estaba: quien pregunta ya mira la pantalla por su cuenta.
  if (state.screen !== 'game') return false;

  // Y con un panel delante tampoco se juega: tapa el tablero lo mismo que el aviso del móvil de pie,
  // así que la partida no corre por debajo mientras se lee — ni el reloj, ni la caja que cae, ni el
  // plátano en el aire.
  //
  // Por el cable, no: el otro extremo está contando el mismo turno con su propio reloj y es él quien
  // lo entrega cuando se le acaba (`passTurn` no pregunta de quién es el turno), así que congelar
  // aquí no para nada — sólo deja esta pantalla mintiendo y la partida esperando a que alguien cierre
  // un panel que la otra máquina no puede ver.
  return heldUpright() || (state.panelOpen && !state.online);
}

/**
 * The splash, then the title. Deliberately short: a splash screen is a courtesy,
 * not a toll booth.
 */
export function bootFrontEnd() {
  showScreen('splash');
  showTip();

  // The gorillas walk in from the corners while the logo is up. It only sets the
  // scene; the render loop drives it, and it outlives the splash on purpose — one
  // and a half seconds would cut it off halfway through a throw.
  startSplashScene();

  clearTimeout(splashTimer);
  splashTimer = setTimeout(() => {
    if (state.screen === 'splash') showScreen('title');
  }, SPLASH_MS);
}

/**
 * A tip while it loads. Nothing is loading — the whole game is already in memory,
 * which is exactly what makes it worth doing.
 */
function showTip() {
  if (!splashTipEl) return;

  splashTipEl.textContent = t('tip.' + Math.floor(Math.random() * SPLASH_TIP_COUNT));

  // And, if the browser is still keeping the music to itself, say the one thing
  // the page cannot do for the player. A wait with no explanation is a bug as far
  // as anybody watching it is concerned.
  if (!splashHintEl) return;

  const waiting = state.musicOn && audioLocked();
  splashHintEl.hidden = !waiting;
  splashHintEl.textContent = waiting ? t('splash.tapForMusic') : '';
}

/** Skips the splash if the player is quicker than it is. */
export function skipSplash() {
  if (state.screen !== 'splash') return false;
  clearTimeout(splashTimer);
  showScreen('title');
  return true;
}

/**
 * "Press start". This is the gesture browsers insist on before audio is allowed
 * to make a sound, so it is also where the audio is unlocked — which means the
 * intro fanfare of the very first round actually plays.
 */
export function pressStart({ stay = false } = {}) {
  initAudio();
  setMusic(state.musicOn);

  // `stay` is for the one caller that has somewhere else to be: the title screen's button
  // also loads a match from a code, and that one wants the audio unlocked without being sent
  // through a menu it is never going to look at.
  if (!stay) showScreen('menu');
  updateMusicLabel();
}
